<template>
  <RevContainer v-if="product" class="bg-surface-default-low">
    <div class="flex flex-col gap-32">
      <RevBreadcrumbs
        :ariaLabel="i18n(translations.breadcrumbAriaLabel)"
        :breads
      />
      <BlockSection
        v-if="cmsSections?.section1?.length"
        :section="cmsSections.section1"
        wrapper-type="div"
      />
      <SeoHero
        :imageUrl="product.image"
        :price="product.price"
        :productName="product.model"
        @navigate-to-funnel="handleNavigateToFunnel"
      />
    </div>
  </RevContainer>
  <RevContainer>
    <div class="flex flex-col gap-56 pt-32 pb-72 md:gap-64">
      <BlockSection
        v-if="cmsSections?.section2?.length"
        :section="cmsSections.section2"
        wrapper-type="div"
      />
      <SeoReassurance />
      <SeoSearchBar @item-selected="handleItemSelected" />
      <BlockSection
        v-if="cmsSections?.section3?.length"
        :section="cmsSections.section3"
        wrapper-type="div"
      />
      <SeoCategories :categories @category-click="handleCategoryClick" />
    </div>
  </RevContainer>

  <RevContainer class="bg-surface-default-low">
    <div class="flex flex-col gap-32">
      <SeoProductLinks
        :sections="[seoSections]"
        @link-click="handleLinkClick"
      />
      <SeoCategoriesTags :categories />
      <BlockSection
        v-if="cmsSections?.section4?.length"
        :section="cmsSections.section4"
        wrapper-type="div"
        class="mb-32"
      />
    </div>
  </RevContainer>
</template>

<script setup lang="ts">
import { createError, navigateTo } from '#imports'

import { CMS_PAGE_SECTION_NAMES } from '@backmarket/http-api/src/api-specs-content/models/cms-sections'
import { useRouteParams } from '@backmarket/nuxt-layer-base/composables/useRouteParams'
import type { BuybackProduct } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/api/types'
import SeoProductLinks from '@backmarket/nuxt-layer-buyback/app/scopes/offer/components/SeoProductLinks/SeoProductLinks.vue'
import { useBuybackCategories } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackCategories/useBuybackCategories'
import { useSeoProductsByCategory } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useSeoProductsByCategory/useSeoProductsByCategory'
import BlockSection from '@backmarket/nuxt-layer-cms/BlockSection.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevBreadcrumbs } from '@ds/components/Breadcrumbs'
import { RevContainer } from '@ds/components/Container'

import SeoCategories from '~/scopes/buyback/components/SeoCategories/SeoCategories.vue'
import SeoCategoriesTags from '~/scopes/buyback/components/SeoCategoriesTags/SeoCategoriesTags.vue'
import SeoHero from '~/scopes/buyback/components/SeoHero/SeoHero.vue'
import SeoReassurance from '~/scopes/buyback/components/SeoReassurance/SeoReassurance.vue'
import SeoSearchBar from '~/scopes/buyback/components/SeoSearchBar/SeoSearchBar.vue'
import { useSeoBreadcrumb } from '~/scopes/buyback/composables/useSeoBreadcrumb'
import { useSeoProductBySlug } from '~/scopes/buyback/composables/useSeoProductBySlug'
import { useSeoProductPageHead } from '~/scopes/buyback/composables/useSeoProductPageHead'
import {
  PRODUCT_PAGE_ERROR_MESSAGE,
  ROUTE_NAMES,
} from '~/scopes/buyback/constants'
import { useFetchFormattedCMSSections } from '~/scopes/cms/composables/useFetchFormattedCMSSections'

import translations from './ProductPage.translations'

const { slug, category } = useRouteParams()
const [{ product, error }, { data: cmsSections }] = await Promise.all([
  useSeoProductBySlug(slug),
  useFetchFormattedCMSSections({
    pageName: CMS_PAGE_SECTION_NAMES.BUYBACK_MODEL_PAGE,
    slug,
    categoryId: category,
  }),
])

const i18n = useI18n()
const { trackClick, trackBlockImpression } = useTracking()
const logger = useLogger()

if (!product.value) {
  logger.error(PRODUCT_PAGE_ERROR_MESSAGE.PRODUCT_NOT_FOUND, {
    error: error.value,
    slug,
    owners: ['bot-squad-circularity-offer-front'],
  })

  throw createError({
    status: 404,
    statusText: i18n(translations.productNotFound),
  })
}

await useSeoProductPageHead(product.value)

const { seoSections } = await useSeoProductsByCategory(
  product.value.category,
  product.value.category === 'smartphone' && product.value.brand === 'Apple'
    ? product.value.brand
    : undefined,
)

const { breads } = useSeoBreadcrumb(
  product.value.category,
  product.value.brand,
  product.value.model,
)

const { categories, getLinkByCategory } = useBuybackCategories()

trackBlockImpression({
  productCategory: product.value.category,
  productBrand: product.value.brand,
  productModel: product.value.model,
  productPrice: product.value.price?.amount ?? '',
})

function createFunnelLink(item: BuybackProduct) {
  const query = {
    brand: item.brand,
    model: item.model,
    model_family: item.productLine,
    nextStep: 'false',
  }

  const linkParams = {
    name: ROUTE_NAMES.DEVICE_ASSESSMENT,
    type: 'internal',
    params: { slug: item.category, id: item.category },
    query: removeEmptyValuesInObject(query),
  }

  return toNuxtLink(linkParams)
}

async function handleItemSelected(item: BuybackProduct) {
  await navigateTo(createFunnelLink(item))
}

async function handleCategoryClick(categoryName: string) {
  await navigateTo(getLinkByCategory(categoryName))
}
function handleLinkClick(productClicked: { label: string; position: number }) {
  trackClick({
    zone: 'buyback_master-pp',
    name: 'footer_link_click',
    value: {
      productName: productClicked.label,
      position: productClicked.position,
    },
  })
}

async function handleNavigateToFunnel() {
  if (!product.value) {
    return
  }

  await navigateTo(createFunnelLink(product.value))

  trackClick({
    zone: 'buyback_master-pp',
    name: 'See Trade In value',
    value: {
      productCategory: product.value.category,
      productBrand: product.value.brand,
      productModel: product.value.model,
      productPrice: product.value.price?.amount ?? '',
    },
  })
}
</script>
